import type { TextInputProps as ExpoTextInputProps } from "@expo/ui";
import React, { useImperativeHandle, useRef, useState } from "react";
import {
  StyleSheet,
  TextInput,
  View,
  type StyleProp,
  type TextInput as RNTextInput,
  type ViewStyle,
} from "react-native";

export type LoginSheetTextInputRef = {
  focus: () => void;
  blur: () => void;
  clear: () => void;
  isFocused: () => boolean;
  setSelection: (start: number, end: number) => Promise<void>;
};

type LoginSheetTextInputProps = ExpoTextInputProps & {
  hostStyle?: StyleProp<ViewStyle>;
};

const LoginSheetTextInput = React.forwardRef<
  LoginSheetTextInputRef,
  LoginSheetTextInputProps
>(function LoginSheetTextInput(
  {
    autoCapitalize,
    autoComplete,
    autoCorrect,
    autoFocus,
    cursorColor,
    editable,
    hostStyle,
    keyboardType,
    onBlur,
    onChangeText,
    onFocus,
    onSubmitEditing,
    placeholder,
    placeholderTextColor,
    returnKeyType,
    secureTextEntry,
    selectionColor,
    style,
    textStyle,
    value,
    testID,
  },
  ref,
) {
  const inputRef = useRef<RNTextInput>(null);
  const valueRef = useRef(value);
  valueRef.current = value;
  const fallbackInitialValueRef = useRef("");
  const [, setFallbackValue] = useState(fallbackInitialValueRef.current);
  const initialValueRef = useRef(value?.value ?? fallbackInitialValueRef.current);
  const lastValueRef = useRef(initialValueRef.current);

  const setCurrentValue = (nextValue: string) => {
    lastValueRef.current = nextValue;
    if (valueRef.current) {
      valueRef.current.value = nextValue;
    } else {
      setFallbackValue(nextValue);
    }
  };

  useImperativeHandle(ref, () => ({
    focus: () => inputRef.current?.focus(),
    blur: () => inputRef.current?.blur(),
    clear: () => {
      inputRef.current?.clear();
      setCurrentValue("");
    },
    isFocused: () => inputRef.current?.isFocused() ?? false,
    setSelection: (start: number, end: number) => {
      inputRef.current?.setNativeProps({ selection: { start, end } });
      return Promise.resolve();
    },
  }));

  return (
    <View style={hostStyle}>
      <TextInput
        ref={inputRef}
        autoCapitalize={autoCapitalize}
        autoComplete={autoComplete}
        autoCorrect={autoCorrect}
        autoFocus={autoFocus}
        cursorColor={cursorColor}
        defaultValue={initialValueRef.current}
        editable={editable}
        keyboardType={keyboardType}
        onBlur={onBlur}
        onChange={(event) => {
          const nextValue = event.nativeEvent.text;
          if (nextValue === lastValueRef.current) return;
          setCurrentValue(nextValue);
          onChangeText?.(nextValue);
        }}
        onChangeText={(nextValue) => {
          if (nextValue === lastValueRef.current) return;
          setCurrentValue(nextValue);
          onChangeText?.(nextValue);
        }}
        onFocus={onFocus}
        onSubmitEditing={(event) => onSubmitEditing?.(event.nativeEvent.text)}
        placeholder={placeholder}
        placeholderTextColor={placeholderTextColor}
        returnKeyType={returnKeyType}
        secureTextEntry={secureTextEntry}
        selectionColor={selectionColor}
        style={[styles.input, style, textStyle]}
        testID={testID}
      />
    </View>
  );
});

export default LoginSheetTextInput;

const styles = StyleSheet.create({
  input: {
    height: "100%",
    width: "100%",
  },
});
